import React, { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, ChevronRight, ShieldCheck, Building2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import usePlanChange from "@/components/billing/usePlanChange";
import ComparisonTable from "@/components/billing/ComparisonTable";
import ChangeBar from "@/components/billing/ChangeBar";
import ConfirmChangeModal from "@/components/billing/ConfirmChangeModal";
import EnterpriseModal from "@/components/billing/EnterpriseModal";
import { cn, dateLocale } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const fmt = (cents) => new Intl.NumberFormat(dateLocale(), { style: "currency", currency: "EUR" }).format((cents || 0) / 100);
const ACCENT = {
  green: "text-emerald-400",
  blue: "text-sky-400",
  purple: "text-purple-400",
  orange: "text-orange-400",
};

function Reassurance({ trialDays }) {
  const items = [
    [tfr("Sans engagement"), tfr("Résiliez à tout moment")],
    [tfr("Paiement sécurisé"), tfr("Paiements traités par Stripe")],
    [tfr("Évolutif"), tfr("Changez d'offre à tout moment")],
    [tfr("Essai gratuit"), `${trialDays || 14} ${tfr("jours pour essayer")}`],
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5" data-testid="plans-reassurance">
      {items.map(([t, s]) => (
        <div key={t} className="rounded-xl border border-border bg-secondary/30 px-3.5 py-2.5">
          <p className="text-xs font-semibold flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-400/80" aria-hidden />{t}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{s}</p>
        </div>
      ))}
    </div>
  );
}

export default function BillingPlans() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planFlow = usePlanChange();
  const [interval, setIntervalChoice] = React.useState(() => {
    try { return localStorage.getItem("athlynx_billing_interval") || "monthly"; } catch { return "monthly"; }
  });
  const chooseInterval = (iv) => {
    setIntervalChoice(iv);
    try { localStorage.setItem("athlynx_billing_interval", iv); } catch { /* noop */ }
  };

  useEffect(() => {
    const sel = searchParams.get("select");
    if (sel && planFlow.data && sel !== planFlow.current) planFlow.selectPlan(sel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFlow.data]);

  const { data, plans, current, currentPlan, loadError, isTrial } = planFlow;

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 sm:px-7 pt-7 pb-12 space-y-7" data-testid="billing-plans-page">
      {/* Breadcrumb + header */}
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground" aria-label="Fil d'Ariane">
        <Link to="/billing" className="hover:text-foreground transition-colors" data-testid="breadcrumb-billing">{tfr("Facturation")}</Link>
        <ChevronRight className="h-3 w-3" aria-hidden />
        <span className="text-foreground/80">{tfr("Comparer les offres")}</span>
      </nav>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em]">{tfr("Trouvez l'offre adaptée à votre activité")}</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {tfr("Comparez les fonctionnalités Athlynx et choisissez le niveau d'accompagnement qui correspond à vos besoins.")}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {currentPlan && (
            <span className="text-xs px-2.5 py-1 rounded-full border border-border text-foreground/80" data-testid="plans-current-badge">
              {isTrial ? tfr("Essai") : tfr("Offre actuelle")} : {currentPlan.name.replace("Athlynx ", "")}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => navigate(`/billing${planFlow.selected ? `?select=${planFlow.selected}` : ""}`)}
            data-testid="plans-back-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {tfr("Retour à la facturation")}
          </Button>
        </div>
      </header>

      {loadError ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="plans-error">
          {tfr("Impossible de charger le catalogue des offres.")}
          <Button variant="outline" size="sm" className="ml-3" onClick={planFlow.load}>{tfr("Réessayer")}</Button>
        </div>
      ) : !data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="plans-page-skeleton">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-96 rounded-2xl bg-secondary/40 animate-pulse" />)}
        </div>
      ) : (
        <>
          <Reassurance trialDays={data.trial_days} />

          {Object.values(data.payment_links_blocked || {}).some(Boolean) && (
            <div
              className="rounded-xl border border-amber-500/40 bg-amber-500/[0.08] px-4 py-3 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2"
              role="alert"
              data-testid="plans-config-mismatch-alert"
            >
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{tfr("Configuration Stripe incomplète : les liens de paiement ne correspondent pas au mode de la clé Stripe (LIVE/TEST). Les paiements sont désactivés par sécurité jusqu'à correction.")}</span>
            </div>
          )}

          {data.annual_enabled && (
            <div className="flex items-center justify-center gap-2" data-testid="billing-interval-toggle">
              <button
                onClick={() => chooseInterval("monthly")}
                className={cn("px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors",
                  interval === "monthly" ? "border-sky-400/70 bg-sky-500/10 text-sky-600 dark:text-sky-300" : "border-border text-muted-foreground hover:text-foreground")}
                data-testid="interval-monthly-btn"
              >
                {tfr("Mensuel")}
              </button>
              <button
                onClick={() => chooseInterval("annual")}
                className={cn("px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors inline-flex items-center gap-1.5",
                  interval === "annual" ? "border-sky-400/70 bg-sky-500/10 text-sky-600 dark:text-sky-300" : "border-border text-muted-foreground hover:text-foreground")}
                data-testid="interval-annual-btn"
              >
                {tfr("Annuel")}
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">{tfr("2 mois offerts")}</span>
              </button>
            </div>
          )}

          {/* Detailed cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-stretch" role="radiogroup" aria-label={tfr("Offres Athlynx")}>
            {plans.map((p) => {
              const isCurrent = p.id === current;
              const isSelected = p.id === planFlow.selected;
              const monthly = p.prices?.monthly;
              return (
                <div
                  key={p.id}
                  role={p.contact_sales ? undefined : "radio"}
                  aria-checked={p.contact_sales ? undefined : isSelected}
                  tabIndex={p.contact_sales ? undefined : 0}
                  onClick={() => !p.contact_sales && !isCurrent && planFlow.selectPlan(p.id)}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && !p.contact_sales && !isCurrent) {
                      e.preventDefault();
                      planFlow.selectPlan(p.id);
                    }
                  }}
                  data-testid={`plans-detail-card-${p.id}`}
                  className={cn(
                    "relative flex flex-col rounded-2xl border bg-surface p-5 transition-colors min-h-[420px]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    !p.contact_sales && !isCurrent && "cursor-pointer",
                    isSelected
                      ? "border-sky-400/80 ring-1 ring-sky-400/60"
                      : isCurrent
                        ? "border-foreground/30"
                        : "border-border hover:border-foreground/25"
                  )}
                >
                  <div className="flex items-center justify-between gap-2 mb-1 min-h-[20px]">
                    <p className={cn("text-[11px] uppercase tracking-wider font-bold", ACCENT[p.accent] || "text-sky-400")}>
                      {p.name.replace("Athlynx ", "")}
                    </p>
                    <div className="flex items-center gap-1.5">
                      {p.is_recommended && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-300" data-testid="plan-badge-recommended">
                          {tfr("★ Recommandé")}
                        </span>
                      )}
                      {isCurrent && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-border text-foreground/80" data-testid={`plan-badge-current-${p.id}`}>
                          {isTrial ? tfr("Essai en cours") : tfr("Offre actuelle")}
                        </span>
                      )}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold">{p.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1 min-h-[32px]">{tfr(p.tagline)}</p>
                  <div className="mt-3 mb-4">
                    {monthly ? (
                      interval === "annual" && p.prices?.annual ? (
                        <>
                          <p className="flex items-baseline gap-1">
                            <span className="text-3xl font-semibold tabular-nums tracking-tight" data-testid={`plan-price-${p.id}`}>
                              {fmt(Math.round(p.prices.annual.amount / 12))}
                            </span>
                            <span className="text-xs text-muted-foreground">{tfr("/ mois")}</span>
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {tfr("Facturé")} {fmt(p.prices.annual.amount)} {tfr("/ an")}
                            {p.annual_savings_cents ? <span className="text-emerald-400/90"> · {tfr("Économisez")} {fmt(p.annual_savings_cents)} {tfr("/ an")}</span> : null}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="flex items-baseline gap-1">
                            <span className="text-3xl font-semibold tabular-nums tracking-tight" data-testid={`plan-price-${p.id}`}>
                              {fmt(monthly.amount)}
                            </span>
                            <span className="text-xs text-muted-foreground">{tfr("/ mois")}</span>
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{tfr("Facturé mensuellement")}</p>
                        </>
                      )
                    ) : (
                      <>
                        <p className="text-3xl font-semibold tracking-tight" data-testid="plan-price-enterprise">{tfr("Sur devis")}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{tfr("Tarification personnalisée")}</p>
                      </>
                    )}
                  </div>
                  <ul className="space-y-1.5 text-xs flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-1.5">
                        <Check className={cn("h-3.5 w-3.5 shrink-0 mt-[1px]", ACCENT[p.accent] || "text-sky-400")} aria-hidden />
                        <span className="text-foreground/85">{tfr(f)}</span>
                      </li>
                    ))}
                  </ul>
                  {p.roadmap_note && (
                    <p className="text-[10px] text-muted-foreground/70 italic mt-2" data-testid="plan-roadmap-note">{p.roadmap_note}</p>
                  )}
                  <div className="mt-4">
                    {p.contact_sales ? (
                      <Button
                        variant="outline"
                        className="w-full h-11 gap-1.5"
                        onClick={(e) => { e.stopPropagation(); planFlow.setEnterpriseOpen(true); }}
                        data-testid="plan-cta-enterprise"
                      >
                        <Building2 className="h-4 w-4" /> {tfr("Nous contacter")}
                      </Button>
                    ) : isCurrent ? (
                      <Button variant="outline" className="w-full h-11" disabled data-testid={`plan-cta-current-${p.id}`}>
                        {tfr("Offre actuelle")}
                      </Button>
                    ) : planFlow.unavailablePlans.includes(p.id) ? (
                      <Button variant="outline" className="w-full h-11" disabled data-testid={`plan-cta-unavailable-${p.id}`}>
                        {tfr(data.payment_links_blocked?.[p.id] ? "Paiement indisponible — configuration Stripe" : "Paiement bientôt disponible")}
                      </Button>
                    ) : (
                      <Button
                        className={cn("w-full h-11", isSelected && "bg-foreground text-background hover:bg-foreground/90")}
                        variant={isSelected ? "default" : "outline"}
                        onClick={(e) => { e.stopPropagation(); planFlow.selectPlan(p.id); }}
                        data-testid={`plan-cta-${p.id}`}
                      >
                        {data.pending_plan_id === p.id ? tfr("Programmé") : `${tfr("Choisir")} ${p.name.replace("Athlynx ", "")}`}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {data.pending_plan_id && (
            <p className="text-xs text-amber-300/90" data-testid="pending-downgrade-note">
              Changement vers {plans.find((x) => x.id === data.pending_plan_id)?.name} programmé à la fin de la période en cours. Vos données seront conservées.
            </p>
          )}

          <ComparisonTable plans={plans} comparison={data.comparison || []} currentPlanId={current} />

          <ChangeBar
            currentPlan={planFlow.currentPlan}
            selectedPlan={planFlow.selectedPlan}
            changeMode={planFlow.changeMode}
            isTrial={planFlow.isTrial}
            busy={planFlow.busy}
            onCancel={planFlow.clearSelection}
            onConfirm={planFlow.openConfirm}
            onActivate={() => planFlow.activateNow(planFlow.selected)}
          />

          <ConfirmChangeModal
            open={planFlow.confirmOpen}
            onOpenChange={planFlow.setConfirmOpen}
            preview={planFlow.preview}
            plans={plans}
            busy={planFlow.busy}
            onConfirm={planFlow.confirmChange}
          />
          <EnterpriseModal open={planFlow.enterpriseOpen} onClose={() => planFlow.setEnterpriseOpen(false)} />
        </>
      )}
    </div>
  );
}
